import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { ItemTable } from '../../library/abstracts/item-table.js';
import '../../library/fields/d-spinner-robot.js';
import type { TableItemsSubstance } from './table-items-substance';
import type { GestureType } from '../../library/fields/gesture-type';

/**
 *
 * STATUS OK
 */
@customElement('d-substances-table')
export class DSubstancesTable extends ItemTable<TableItemsSubstance> {
  static readonly styles = [
    ...ItemTable.styles,
    css`
      :host {
        display: block;
      }

      .robot {
        display: flex;
        align-items: center;
      }

      d-spinner-robot {
        position: relative;
        display: inline-block;
        width: 40px;
        margin-right: 10px;
      }

      /* TODO Define column widths. Currently setting labels and risk to fixed width */
      td[data-field='name'] {
        width: 300px;
      }
      td[data-field='hazardLabels'] {
        width: 100px;
      }
      td[data-field='applications'] {
        width: 300px;
      }
      td[data-field='risk'] {
        width: 100px;
      }

      td[data-field='hazardLabels'] table {
        display: block;
        margin: 4px 40px 4px 6px;
        width: 100%;
      }

      td[data-field='hazardLabels'] table tr {
        display: inline-block;
        margin: -10px;
      }

      td[data-field='hazardLabels'] table tr:last-child {
        margin-right: 10px;
      }

      td[data-field='hazardLabels'] table td {
        display: inline-block;
        border: none;
        width: 40px;
        height: 40px;
        background-size: contain;
        background-position: 50% 50%;
        background-repeat: no-repeat;
        font-size: 0;
      }

      td[data-value='GHS01'] {
        background-image: url(/images/ghs/ghs-01-explosive.svg);
      }

      td[data-value='GHS02'] {
        background-image: url(/images/ghs/ghs-02-flammable.svg);
      }

      td[data-value='GHS03'] {
        background-image: url(/images/ghs/ghs-03-oxidizing.svg);
      }

      td[data-value='GHS04'] {
        background-image: url(/images/ghs/ghs-04-compressed-gas.svg);
      }

      td[data-value='GHS05'] {
        background-image: url(/images/ghs/ghs-05-corrosive.svg);
      }

      td[data-value='GHS06'] {
        background-image: url(/images/ghs/ghs-06-toxic.svg);
      }

      td[data-value='GHS07'] {
        background-image: url(/images/ghs/ghs-07-harmful.svg);
      }

      td[data-value='GHS08'] {
        background-image: url(/images/ghs/ghs-08-health-hazard.svg);
      }

      td[data-value='GHS09'] {
        background-image: url(/images/ghs/ghs-09-enviromental-hazard.svg);
      }

      th[data-field='risk'] d-label {
        display: none;
      }

      th[data-field='risk']:after {
        content: 'Risiko ikke angitt'; /* i18n-exempt: CSS ::before risiko-etikett */
        display: inline-block;
        border-radius: 16px;
        background: silver;
        padding: 6px 10px;
        color: white;
        font-family: var(--mainSans), sans-serif;
        font-size: 18px;
        font-weight: 500;
      }

      td[data-field='risk'],
      tr:hover td[data-field='risk'] {
        font-size: 0;
      }

      td[data-field='risk'] d-label,
      tr:hover td[data-field='risk'] d-label {
        font-size: 15px;
      }

      td[data-field='risk']:after {
        content: '';
        display: inline-block;
        margin: -2px 0;
        border-radius: 13px;
        padding: 4px 8px;
        color: white;
        font-family: var(--mainSans), sans-serif;
        font-size: 13px;
        font-weight: 500;
      }

      th[data-field='risk'][data-value='1']:after,
      td[data-field='risk'][data-value='1']:after {
        background: var(--risk-color-1);
      }

      th[data-field='risk'][data-value='2']:after,
      td[data-field='risk'][data-value='2']:after {
        background: var(--risk-color-2);
      }

      th[data-field='risk'][data-value='3']:after,
      td[data-field='risk'][data-value='3']:after {
        background: var(--risk-color-3);
      }

      th[data-field='risk'][data-value='4']:after,
      td[data-field='risk'][data-value='4']:after {
        background: var(--risk-color-4);
      }

      th[data-field='risk'][data-value='1']:after {
        content: 'Lav risiko'; /* i18n-exempt: CSS ::before risiko-etikett */
      }

      th[data-field='risk'][data-value='2']:after {
        content: 'Moderat risiko'; /* i18n-exempt: CSS ::before risiko-etikett */
      }

      th[data-field='risk'][data-value='3']:after {
        content: 'Høy risiko'; /* i18n-exempt: CSS ::before risiko-etikett */
      }

      th[data-field='risk'][data-value='4']:after {
        content: 'Svært høy risiko'; /* i18n-exempt: CSS ::before risiko-etikett */
      }

      td[data-field='risk'][data-value='1']:after {
        content: 'Lav';
      }

      td[data-field='risk'][data-value='2']:after {
        content: 'Moderat';
      }

      td[data-field='risk'][data-value='3']:after {
        content: 'Høy'; /* i18n-exempt: CSS ::before risiko-etikett */
      }

      td[data-field='risk'][data-value='4']:after {
        content: 'Svært høy'; /* i18n-exempt: CSS ::before risiko-etikett */
      }

      @media only screen and (max-width: 700px) {
        td[data-field='hazardLabels'] table {
          margin: 8px;
        }
      }
    `,
  ];
  @property({ type: Array })
  items: TableItemsSubstance[] = [];
  @property({ type: Boolean, attribute: 'no-animation' })
  noAnimation = false;

  protected get entityType() {
    return 'substances';
  }

  protected get sortGroupsDescending(): boolean {
    return this.groupBy === 'uploaded' || this.groupBy === 'risk';
  }

  /**
   * No special grouping needed - queued items have proper uploadedSortValue
   * from their batchUploadDateTime, so they group naturally with normal logic.
   * Items in the same batch share the same datetime and will be in the same group.
   */

  includeItem(item: TableItemsSubstance): boolean {
    return this.filterFields(item.name, item.applications);
  }

  pendingRow(item: TableItemsSubstance, colspan: number) {
    let gesture: GestureType[];
    let message: string;
    let statusSuffix: string;
    let isClickable = false;
    const displayName = item.name || t('substances.unknownFile');

    switch (item.state) {
      case 'queued':
        gesture = ['upload'];
        message = displayName;
        statusSuffix = t('substances.uploadStatusWaiting');
        break;

      case 'uploading':
        gesture = ['upload'];
        message = displayName;
        statusSuffix =
          item.retryCount && item.retryCount > 1
            ? t('substances.uploadStatusUploadingRetry', { count: item.retryCount })
            : t('substances.uploadStatusUploading');
        break;

      case 'failed':
        gesture = ['blink', 'surprised'];
        message = displayName;
        statusSuffix = item.statusMessage || t('substances.uploadStatusFailed');
        if (item.retryCount && item.retryCount > 1) {
          statusSuffix += t('substances.uploadStatusRetrySuffix', { count: item.retryCount });
        }
        break;

      case 'duplicate':
        gesture = ['blink'];
        message = displayName;
        statusSuffix = t('substances.uploadStatusDuplicate');
        break;

      case 'unreadable':
        gesture = ['blink', 'surprised'];
        message = displayName;
        statusSuffix = t('substances.uploadStatusUnreadable');
        isClickable = true;
        break;

      case 'pending':
      default:
        gesture = ['readHorizontal'];
        message = displayName;
        statusSuffix = t('substances.uploadStatusReading');
        break;
    }

    const fullMessage = statusSuffix ? `${message} – ${statusSuffix}` : message;

    if (isClickable) {
      return html`
        <tr class="click-elm item" href="${item.href}" @click=${(e) => this.rowClick(item.href, e)}>
          <td colspan="${colspan}">
            <div class="robot">
              <d-spinner-robot .gesture=${gesture} size="40" ?no-animation=${this.noAnimation}></d-spinner-robot>
              <span>${fullMessage}</span>
            </div>
          </td>
        </tr>
      `;
    }

    return html`
      <tr>
        <td colspan="${colspan}">
          <div class="robot">
            <d-spinner-robot .gesture=${gesture} size="40" ?no-animation=${this.noAnimation}></d-spinner-robot>
            <span>${fullMessage}</span>
          </div>
        </td>
      </tr>
    `;
  }

  override renderRow(item, columns) {
    return html`
      ${item.state ? html` ${this.pendingRow(item, columns.length)} ` : html` ${super.renderRow(item, columns)} `}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-substances-table': DSubstancesTable;
  }
}
